"use client";

import { useEffect, useRef, useState } from "react";
import { BrandMark } from "@/components/ui/design-system/brand-mark";
import { cn } from "@/lib/utils";
import type { CreatorFeedItem } from "./utils";

/**
 * The picture a request is found by, in one of two frames.
 *
 * `fill` is a 9:16 tile the cover ad fills, for a row. `stage` is a 4:3
 * frame the ad fills the same way, for the feed's cards, which put the
 * picture last, full-bleed and as the larger half of the card. Filling a
 * landscape frame keeps the middle of a 9:16 ad and crops the top and the
 * bottom; the picture is a picture here, and the whole ad is one tap away.
 *
 * Framework ads have no stored thumbnail, so the first frame is the poster:
 * `preload="metadata"` fetches enough to paint it and no more, and the
 * `#t=` fragment on the source seeks a hair past zero, because a browser
 * that has only metadata does not promise to paint frame zero at all (a
 * blank tile until the pointer arrives) and a seek is what makes it draw.
 * A caller that has a still (a creator submission's `posterUrl`) passes it
 * and the video loads nothing until it plays, so a grid of covers costs one
 * image each rather than a media request each.
 *
 * Under a pointer the ad plays, muted, and rewinds when the pointer leaves.
 * Pointer only: a touch has no hover, and a tap is the press on the card
 * around it. Off under reduced motion, where a grid of silently moving
 * pictures is the thing the setting asks not to see. The frame itself is
 * `aria-hidden`: the card's words already say what it is.
 *
 * With no cover ad the frame is the brand's: its logo filling the frame
 * and blurred into a wash, so the tile takes the brand's colour the way a
 * lineup's tiles each have their own ground, and the mark itself set on
 * that wash at the size of an app icon. A brand with no logo gets the
 * wash of its initial's fill, which is the same thing one step plainer.
 * Nothing here is a picked colour; it is all the brand's own.
 */
export function RequestCover({
  item,
  posterUrl,
  layout = "fill",
  className,
}: {
  item: Pick<CreatorFeedItem, "cover" | "orgName" | "orgLogoUrl">;
  /** A still for the cover video, shown at rest in place of its first frame. */
  posterUrl?: string | null;
  layout?: "fill" | "stage";
  /** The width (and for `fill`, the height follows from 9:16). */
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(
      "(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)",
    );
    const update = () => setCanHover(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const play = () => {
    if (!canHover) return;
    void videoRef.current?.play().catch(() => {});
  };
  const rest = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    // Back to the poster frame: the same hair past zero the fragment asks
    // for, so the tile at rest is the tile it started as. With a `posterUrl`
    // it rests on that frame rather than the still: reloading to get the
    // still back would fetch the file again on the next hover.
    video.currentTime = 0.001;
  };
  const src = item.cover ? `${item.cover.videoUrl}#t=0.001` : null;

  return (
    <span
      aria-hidden
      onPointerEnter={play}
      onPointerLeave={rest}
      className={cn(
        "relative block shrink-0 overflow-hidden bg-nf-ds-bg-muted",
        layout === "fill"
          ? "aspect-[9/16] rounded-nf-ds-md ring-1 ring-nf-ds-surface-card-ring ring-inset"
          : "aspect-[4/3]",
        className,
      )}
    >
      {src ? (
        <video
          ref={videoRef}
          src={src}
          poster={posterUrl ?? undefined}
          className="absolute inset-0 size-full object-cover"
          muted
          playsInline
          loop
          preload={posterUrl ? "none" : "metadata"}
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          {/* The wash: the logo (or the initial's fill) as the whole frame,
              blurred past recognition. Its fallback letter blurs into the
              same wash, which is why it can be the mark component twice. */}
          <BrandMark
            name={item.orgName}
            imageUrl={item.orgLogoUrl}
            className="absolute inset-0 size-full object-cover opacity-70 blur-3xl saturate-150"
            fallbackClassName="bg-nf-ds-brand text-transparent"
          />
          <BrandMark
            name={item.orgName}
            imageUrl={item.orgLogoUrl}
            className={cn(
              "relative rounded-full shadow-nf-ds-lg ring-nf-ds-surface-card",
              layout === "fill"
                ? "size-8 ring-2"
                : "size-nf-ds-20 ring-4 lg:size-nf-ds-24",
            )}
            fallbackClassName={cn(
              "bg-nf-ds-brand",
              layout === "fill" ? "text-nf-ds-xs" : "text-nf-ds-2xl",
            )}
          />
        </span>
      )}
    </span>
  );
}
